import { List, fromJS } from 'immutable';
import pipe from 'transmute/pipe';
import { ChannelTypes } from 'conversations-internal-schema/constants/ChannelTypes';
import * as RoutingTypes from '../constants/RoutingTypes';
import { isValid } from '../operators/isValid';
import { getFallback, getTeamIds, getType, getUserIds } from '../operators/routingRulesGetters';
import { setFallback, setTeamIds, setUserIds } from '../operators/routingRulesSetters';
import AssignToAvailableCheckbox from './AssignToAvailableCheckbox';
import FallbackRow from './FallbackRow';
import RoundRobinRow from './RoundRobinRow';
import RoutingRulesSelectRow from './RoutingRulesSelectRow';
import { useCallback } from 'react';
import styled from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RoutingRulesWrapper = styled.div.withConfig({
  displayName: "RoutingRules__RoutingRulesWrapper",
  componentId: "sc-149arzx-0"
})(["width:100%;"]);
const RoutingRules = ({
  disableKeepAssigneeOption,
  FormControl,
  routingRules,
  hasRoutingAccess,
  onOpenChange,
  onRuleUpdate,
  teams,
  users,
  currentUser,
  disabled,
  inboxId,
  isLoadingUsersAndTeams,
  onUsersAndTeamsMount,
  aiChatbotId,
  showAnyUserOption,
  showKeepAssigneeOption,
  showUnassignedOption = true,
  showOwnerOption = true,
  showSelectionRow = true,
  teamTree,
  userAndTeamSelectValueComponent,
  use,
  trackUsage,
  workSpace,
  hideAvailableUserCheckbox,
  isUngatedForHelpDeskRoutingScopeFiltering,
  hasEditAccess
}) => {
  const immutableUser = fromJS(currentUser);
  const usersAndTeamsSelectError = !isValid(routingRules);
  const anyAvailableUser = use === ChannelTypes.CHAT || use === ChannelTypes.FACEBOOK_MESSENGER;
  const routingRuleType = getType(routingRules);
  const showFallbackRow = RoutingTypes.isFallbackType(routingRuleType);
  const fallbackRoutingRules = getFallback(routingRules);
  const onChange = useCallback((userIds, teamIds) => {
    const setIds = pipe(setUserIds(userIds), setTeamIds(teamIds));
    if (showFallbackRow) {
      const fallback = setIds(fallbackRoutingRules);
      onRuleUpdate(setFallback(fallback, routingRules));
    } else {
      onRuleUpdate(setIds(routingRules));
    }
  }, [showFallbackRow, onRuleUpdate, routingRules, fallbackRoutingRules]);
  const maybeRoundRobinRoutingRules = showFallbackRow ? fallbackRoutingRules : routingRules;
  const showRoundRobinRow = getType(maybeRoundRobinRoutingRules) === RoutingTypes.USERS_AND_TEAMS;
  return /*#__PURE__*/_jsxs(RoutingRulesWrapper, {
    children: [showSelectionRow && /*#__PURE__*/_jsx(RoutingRulesSelectRow, {
      anyAvailableUser: anyAvailableUser,
      disabled: disabled,
      disableKeepAssigneeOption: disableKeepAssigneeOption,
      FormControl: FormControl,
      hasRoutingAccess: hasRoutingAccess,
      aiChatbotId: aiChatbotId == null ? undefined : aiChatbotId // we treat null and undefined aiChatbotId to be no aiChatbotId
      ,
      showAnyUserOption: showAnyUserOption,
      showKeepAssigneeOption: showKeepAssigneeOption,
      showUnassignedOption: showUnassignedOption,
      showOwnerOption: showOwnerOption,
      routingRules: routingRules,
      onOpenChange: onOpenChange,
      onRuleUpdate: onRuleUpdate,
      workSpace: workSpace,
      isUngatedForHelpDeskRoutingScopeFiltering: isUngatedForHelpDeskRoutingScopeFiltering,
      hasEditAccess: hasEditAccess
    }), showFallbackRow && /*#__PURE__*/_jsx(FallbackRow, {
      disabled: disabled,
      FormControl: FormControl,
      routingRules: routingRules,
      onRuleUpdate: onRuleUpdate,
      hasRoutingAccess: hasRoutingAccess,
      isUngatedForHelpDeskRoutingScopeFiltering: isUngatedForHelpDeskRoutingScopeFiltering
    }), showRoundRobinRow && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(RoundRobinRow, {
        routingRules: routingRules,
        disabled: disabled,
        FormControl: FormControl,
        onUserOrTeamSelect: onChange,
        userAndTeamSelectValueComponent: userAndTeamSelectValueComponent,
        teamsOptions: teams,
        usersOptions: users,
        teamTree: teamTree,
        isLoading: isLoadingUsersAndTeams,
        currentUser: immutableUser,
        onUsersAndTeamsMount: onUsersAndTeamsMount,
        selectedTeamIds: getTeamIds(maybeRoundRobinRoutingRules) || List(),
        selectedUserIds: getUserIds(maybeRoundRobinRoutingRules) || List(),
        usersAndTeamsSelectError: usersAndTeamsSelectError,
        trackUsage: trackUsage,
        showLabel: false,
        hasRoutingAccess: hasRoutingAccess,
        inboxId: inboxId,
        workSpace: workSpace,
        isUngatedForHelpDeskRoutingScopeFiltering: isUngatedForHelpDeskRoutingScopeFiltering
      }), !hideAvailableUserCheckbox && /*#__PURE__*/_jsx(AssignToAvailableCheckbox, {
        channelType: use,
        disabled: disabled,
        routingRules: routingRules,
        onRuleUpdate: onRuleUpdate
      })]
    })]
  });
};
RoutingRules.displayName = 'RoutingRules';
RoutingRules.defaultProps = {
  trackUsage: () => {}
};
export default RoutingRules;